import { useEffect, useState, Fragment } from "react";
import { Download } from "lucide-react";
import api, { API } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";

export default function AdminOrders() {
  const { t } = useLang();
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/admin/orders").then((r) => setOrders(r.data));
  }, []);

  const downloadInvoice = async (orderId) => {
    const res = await fetch(`${API}/orders/${orderId}/invoice`, { credentials: "include" });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `factura-${orderId.slice(0, 8)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-orders-page">
      <header className="mb-8">
        <p className="tracking-eyebrow text-[var(--eafg-muted)]">Ventas</p>
        <h1 className="font-serif-display text-5xl mt-2">{t("admin.orders")}</h1>
      </header>

      <div className="border border-[var(--eafg-line)] bg-white">
        <table className="w-full text-left">
          <thead>
            <tr className="tracking-eyebrow text-[0.7rem] text-[var(--eafg-muted)] border-b border-[var(--eafg-line)]">
              <th className="p-4">Pedido</th>
              <th className="p-4">Email</th>
              <th className="p-4">Fecha</th>
              <th className="p-4 text-right">Total</th>
              <th className="p-4 text-center">Estado</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <Fragment key={o.id}>
                <tr className="border-b border-[var(--eafg-line)]">
                  <td className="p-4 font-mono text-xs">{o.id.slice(0, 8).toUpperCase()}</td>
                  <td className="p-4 text-sm">{o.email}</td>
                  <td className="p-4 text-sm text-[var(--eafg-muted)]">
                    {new Date(o.created_at).toLocaleString()}
                  </td>
                  <td className="p-4 text-right tabular-nums">{o.amount_total.toFixed(2)} €</td>
                  <td className="p-4 text-center">
                    <span className={`text-xs px-2 py-1 border ${o.status === "paid" ? "border-[var(--eafg-copper)] text-[var(--eafg-copper)]" : "border-[var(--eafg-line)] text-[var(--eafg-muted)]"}`}>
                      {o.status}
                    </span>
                  </td>
                  <td className="p-4 text-right space-x-3">
                    {o.status === "paid" && (
                      <button
                        onClick={() => downloadInvoice(o.id)}
                        className="text-sm tracking-eyebrow hover:text-[var(--eafg-copper)] inline-flex items-center gap-1"
                        data-testid={`admin-invoice-${o.id}`}
                        title="Factura PDF"
                      >
                        <Download size={14} strokeWidth={1.5} />
                      </button>
                    )}
                    <button
                      onClick={() => setOpen(open === o.id ? null : o.id)}
                      className="text-sm tracking-eyebrow hover:text-[var(--eafg-copper)]"
                      data-testid={`admin-order-toggle-${o.id}`}
                    >
                      {open === o.id ? "−" : "+"}
                    </button>
                  </td>
                </tr>
                {open === o.id && (
                  <tr className="bg-[#fafaf9] border-b border-[var(--eafg-line)]">
                    <td colSpan={6} className="p-6">
                      <div className="grid md:grid-cols-2 gap-8">
                        <div>
                          <h4 className="tracking-eyebrow text-[var(--eafg-muted)] mb-3">Productos</h4>
                          <ul className="space-y-2">
                            {o.items.map((it, idx) => (
                              <li key={idx} className="flex justify-between border-b border-[var(--eafg-line)] py-2">
                                <span>{it.name} × {it.quantity}</span>
                                <span className="tabular-nums">{it.line_total.toFixed(2)} €</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="tracking-eyebrow text-[var(--eafg-muted)] mb-3">Envío</h4>
                          <p>{o.shipping?.name}</p>
                          <p>{o.shipping?.address}</p>
                          <p>{o.shipping?.postal_code} {o.shipping?.city}</p>
                          <p>{o.shipping?.country}</p>
                          <p className="mt-2 text-[var(--eafg-muted)]">{o.shipping?.email} · {o.shipping?.phone}</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {orders.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-[var(--eafg-muted)]">Sin pedidos.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
